import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { ToastService } from 'src/app/services/toast.service';
import { ssoConnectionsService } from 'src/app/shared/vive-api/sso-connections.service';
import { SsoConnectionDto } from 'src/app/shared/vive-api/vive-api-models';

import { SSO_SAVE_FAILED, ssoApiErrorMessage } from '../../sso-connections';

// The API's limit on one save.
const MAX_DOMAINS = 50;

const savedDomains = (connection: SsoConnectionDto): string[] =>
  (connection.Domains ?? []).map(({ domain }) => domain);

@Component({
  selector: 'app-sso-connection-domains-card',
  templateUrl: './sso-connection-domains-card.component.html',
  styleUrl: './sso-connection-domains-card.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SsoConnectionDomainsCardComponent implements OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly toastService = inject(ToastService);

  @Input({ required: true }) public connection!: SsoConnectionDto;
  @Output() public readonly saved = new EventEmitter<SsoConnectionDto>();

  public readonly maxDomains = MAX_DOMAINS;
  public domains: string[] = [];
  public newDomain = '';
  public saving = false;
  public error: string | null = null;

  public get canSave(): boolean {
    const saved = savedDomains(this.connection);

    return (
      !this.saving &&
      (saved.length !== this.domains.length ||
        saved.some((domain) => !this.domains.includes(domain)))
    );
  }

  // Seeds the list once. Later inputs come from the other cards' saves and the
  // header's activate or disable, and must not wipe domains added here but not
  // saved yet.
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['connection']?.firstChange) {
      this.domains = savedDomains(this.connection);
    }
  }

  public add(): void {
    // The API stores domains lower-cased and trimmed, and so does this list, so a
    // repeat is caught here.
    const domain = this.newDomain.trim().toLowerCase();

    if (
      domain !== '' &&
      !this.domains.includes(domain) &&
      this.domains.length < MAX_DOMAINS
    ) {
      this.domains = [...this.domains, domain];
    }

    this.newDomain = '';
  }

  public remove(domain: string): void {
    this.domains = this.domains.filter((kept) => kept !== domain);
  }

  public async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }

    this.saving = true;
    this.error = null;
    this.cdr.markForCheck();

    try {
      const updated = await ssoConnectionsService.replaceDomains({
        pathParams: { connectionId: this.connection.id },
        data: { domains: this.domains }
      });
      this.connection = updated;
      this.domains = savedDomains(updated);
      this.saved.emit(updated);
      this.toastService.addSuccessToast('Saved', 'Your changes are saved.');
    } catch (error) {
      this.error = ssoApiErrorMessage(error, SSO_SAVE_FAILED);
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }
}
